/**
 * SPA mirror of `shared/src/util/next-pay-dates.ts`. Pure date arithmetic
 * — duplicated here because web/ does not import from `@hearth/shared`
 * at the root barrel (5.1 lock; only the `@hearth/shared/util` subpath
 * is documented-allowed exception, but the recurring-paycheck cadence
 * helper is small enough to mirror inline).
 */

import type {
  IncomeSchedule,
  PaycheckFrequency,
} from "../api-modules/types.js";

interface YMD {
  readonly y: number;
  readonly m: number;
  readonly d: number;
}

function parseIso(iso: string): YMD {
  const parts = iso.split("-");
  return { y: Number(parts[0]), m: Number(parts[1]), d: Number(parts[2]) };
}

function formatIso(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function addDays(iso: string, days: number): string {
  const { y, m, d } = parseIso(iso);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function addMonths(iso: string, months: number): string {
  const { y, m, d } = parseIso(iso);
  const totalMonths = (m - 1) + months;
  const newY = y + Math.floor(totalMonths / 12);
  const newM = (((totalMonths % 12) + 12) % 12) + 1;
  const last = lastDayOfMonth(newY, newM);
  const newD = Math.min(d, last);
  return formatIso(newY, newM, newD);
}

const minDate = (a: string, b: string) => (a < b ? a : b);
const maxDate = (a: string, b: string) => (a > b ? a : b);

export type ScheduleCadenceFields = Pick<
  IncomeSchedule,
  | "frequency"
  | "anchor_date"
  | "semi_monthly_days"
  | "effective_from"
  | "end_date"
>;

export function nextPayDates(
  schedule: ScheduleCadenceFields,
  fromIso: string,
  throughIso: string,
): readonly string[] {
  if (!schedule.frequency) return [];
  if (fromIso > throughIso) return [];
  const start = maxDate(fromIso, schedule.effective_from);
  const end = schedule.end_date
    ? minDate(throughIso, schedule.end_date)
    : throughIso;
  if (start > end) return [];

  switch (schedule.frequency) {
    case "weekly":
    case "bi_weekly": {
      if (!schedule.anchor_date) return [];
      const period = schedule.frequency === "weekly" ? 7 : 14;
      let pay = schedule.anchor_date;
      while (pay < start) pay = addDays(pay, period);
      const out: string[] = [];
      while (pay <= end) {
        out.push(pay);
        pay = addDays(pay, period);
      }
      return out;
    }
    case "semi_monthly": {
      if (!schedule.semi_monthly_days) return [];
      const [rawD1, rawD2] = schedule.semi_monthly_days;
      const [d1, d2] = rawD1 <= rawD2 ? [rawD1, rawD2] : [rawD2, rawD1];
      const out: string[] = [];
      const startP = parseIso(start);
      const endP = parseIso(end);
      let y = startP.y;
      let m = startP.m;
      while (y < endP.y || (y === endP.y && m <= endP.m)) {
        const last = lastDayOfMonth(y, m);
        const day1 = Math.min(d1, last);
        const day2 = Math.min(d2, last);
        const days = day1 === day2 ? [day1] : [day1, day2];
        for (const dd of days) {
          const iso = formatIso(y, m, dd);
          if (iso >= start && iso <= end) out.push(iso);
        }
        m += 1;
        if (m > 12) {
          m = 1;
          y += 1;
        }
      }
      return out;
    }
    case "monthly": {
      if (!schedule.anchor_date) return [];
      const anchorP = parseIso(schedule.anchor_date);
      const out: string[] = [];
      const startP = parseIso(start);
      const endP = parseIso(end);
      let y = startP.y;
      let m = startP.m;
      while (y < endP.y || (y === endP.y && m <= endP.m)) {
        const last = lastDayOfMonth(y, m);
        const day = Math.min(anchorP.d, last);
        const iso = formatIso(y, m, day);
        if (iso >= start && iso <= end) out.push(iso);
        m += 1;
        if (m > 12) {
          m = 1;
          y += 1;
        }
      }
      return out;
    }
    case "quarterly":
    case "annual": {
      if (!schedule.anchor_date) return [];
      const step = schedule.frequency === "quarterly" ? 3 : 12;
      let pay = schedule.anchor_date;
      while (pay < start) pay = addMonths(pay, step);
      const out: string[] = [];
      while (pay <= end) {
        out.push(pay);
        pay = addMonths(pay, step);
      }
      return out;
    }
  }
}

export function payPeriodsPerYear(freq: PaycheckFrequency): number {
  switch (freq) {
    case "weekly":
      return 52;
    case "bi_weekly":
      return 26;
    case "semi_monthly":
      return 24;
    case "monthly":
      return 12;
    case "quarterly":
      return 4;
    case "annual":
      return 1;
  }
}
